import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { queryKeys, useApiQuery } from '../../api/hooks';
import type { ReportSummaryResponse, ReportTableResponse } from '../../api/types';
import { AsyncSection } from '../../components/AsyncSection';
import { PageHeader } from '../../components/PageHeader';
import { useLocalisedName } from '../../i18n/localisedName';
import { useBranchPicker } from '../scheduling/useBranchPicker';
import { useApiClient } from '../../api/apiContext';
import { Icon } from '../../components/Icon';
import { ReportChart } from './ReportChart';
import { ReportTable } from './ReportTable';
import { dateTime } from '../../i18n/format';
import { exportFilename, exportPath, reportHeading } from './reportFormat';

/**
 * The reports hub (FR-1101, §21, DEC-046).
 *
 * One screen for all eight, because the API answers them all in one shape — named columns, rows
 * keyed by column, a totals row. A hub built as eight bespoke screens would have eight table
 * layouts to keep consistent and would need a ninth written before a ninth report could ship.
 *
 * The catalogue is fetched rather than hard-coded: the server already knows which reports this
 * account may open, and duplicating that list here would be a second place for the permission
 * matrix to be wrong. A doctor sees one entry; a receptionist reaches this screen only if some
 * future role grants a report permission, and would see whatever that permission opens.
 */
export function ReportsPage() {
  const { t, i18n } = useTranslation();
  const { name } = useLocalisedName();
  const { clinicId, branchId, branches, choose } = useBranchPicker();
  const client = useApiClient();
  const [exporting, setExporting] = useState(false);

  const [selected, setSelected] = useState<string | null>(null);
  const [dimension, setDimension] = useState<string | null>(null);
  const [allBranches, setAllBranches] = useState(true);
  const [{ from, to }, setPeriod] = useState(() => defaultMonth());

  const catalogue = useApiQuery<ReportSummaryResponse[]>(
    queryKeys.reportCatalogue(clinicId ?? ''),
    `/clinics/${clinicId}/reports`,
    { enabled: Boolean(clinicId) },
  );

  const reports = useMemo(() => catalogue.data ?? [], [catalogue.data]);
  const active = reports.find((report) => report.key === selected) ?? reports[0];
  const activeDimension = dimension ?? active?.defaultDimension ?? null;

  const query = `${active?.key ?? ''}:${from}:${to}:${activeDimension ?? ''}:${allBranches ? 'all' : branchId}`;
  const path = active
    ? `/clinics/${clinicId}/reports/${active.key}?from=${from}&to=${to}` +
      (activeDimension ? `&dimension=${activeDimension}` : '') +
      (!allBranches && branchId ? `&branchId=${branchId}` : '')
    : '';

  const table = useApiQuery<ReportTableResponse>(queryKeys.report(clinicId ?? '', query), path, {
    enabled: Boolean(clinicId && active),
  });

  if (!clinicId) {
    return <PageHeader title={t('nav.items.reports')} description={t('home.noClinic')} />;
  }

  return (
    <>
      <PageHeader title={t('nav.items.reports')} description={t('reports.description')} />

      <div className="filters">
        <label className="field">
          <span className="field__label">{t('reports.report')}</span>
          <select
            value={active?.key ?? ''}
            onChange={(event) => {
              setSelected(event.target.value);
              // The new report may not offer the dimension the old one was grouped by, and asking
              // for one it does not offer is refused by the API rather than ignored.
              setDimension(null);
            }}
          >
            {reports.map((report) => (
              <option key={report.key} value={report.key}>
                {t(`reports.keys.${report.key}`)}
              </option>
            ))}
          </select>
        </label>

        <label className="field">
          <span className="field__label">{t('reports.groupBy')}</span>
          <select
            value={activeDimension ?? ''}
            onChange={(event) => setDimension(event.target.value)}
            disabled={!active}
          >
            {(active?.dimensions ?? []).map((option) => (
              <option key={option} value={option}>
                {t(`reports.dimensions.${option}`)}
              </option>
            ))}
          </select>
        </label>

        <label className="field">
          <span className="field__label">{t('reports.from')}</span>
          <input type="date" value={from} onChange={(event) => setPeriod({ from: event.target.value, to })} />
        </label>
        <label className="field">
          <span className="field__label">{t('reports.to')}</span>
          <input type="date" value={to} onChange={(event) => setPeriod({ from, to: event.target.value })} />
        </label>

        <label className="field checkbox">
          <input type="checkbox" checked={allBranches} onChange={(event) => setAllBranches(event.target.checked)} />
          <span>{t('reports.allBranches')}</span>
        </label>
        {!allBranches ? (
          <label className="field">
            <span className="field__label">{t('board.branch')}</span>
            <select value={branchId ?? ''} onChange={(event) => choose(event.target.value)}>
              {branches.map((branch) => (
                <option key={branch.id} value={branch.id}>
                  {name(branch, branch.code)}
                </option>
              ))}
            </select>
          </label>
        ) : null}
      </div>

      <AsyncSection
        query={table}
        skeletonRows={6}
        isEmpty={(data) => (data.rows ?? []).length === 0}
        emptyTitle={t('reports.emptyTitle')}
        emptyDescription={t('reports.emptyDescription')}
      >
        {(data) => (
          <section className="panel">
            <header className="panel__header">
              <div className="panel__heading" style={{ marginBlockEnd: 0 }}>
                <h2>{t(`reports.keys.${data.key}`)}</h2>
                <p className="muted">
                  {t('reports.generatedAt', {
                    at: dateTime(data.generatedAt ?? new Date().toISOString(), i18n.language),
                  })}
                </p>
              </div>
              <div className="table__actions">
                {data.redacted ? <span className="badge badge--muted">{t('reports.redacted')}</span> : null}
                {/*
                  Fetched rather than linked. An anchor cannot carry the bearer token, and putting
                  it in the query string would write a credential into every proxy log on the way.
                  The headings travel with the request because the server holds no message
                  catalogue — every string here is translated on this side (NFR-006), and a CSV
                  with English headings inside an Arabic dashboard would be the one place that
                  stopped being true.
                */}
                <button
                  type="button"
                  className="button"
                  disabled={exporting}
                  aria-busy={exporting}
                  onClick={() => {
                    const filters = {
                      from,
                      to,
                      dimension: activeDimension,
                      branchId: allBranches ? null : branchId,
                    };
                    setExporting(true);
                    void client
                      .download(exportPath(clinicId, data, filters, t))
                      .then((blob) => saveBlob(blob, exportFilename(data, filters)))
                      .finally(() => setExporting(false));
                  }}
                >
                  {exporting ? (
                    <span className="spinner" aria-hidden="true" />
                  ) : (
                    <Icon name="download" size={18} />
                  )}
                  {exporting ? t('reports.exporting') : t('reports.export')}
                </button>
              </div>
            </header>

            {/* The comparison first, then the figures it was read from. A `key` on the chart, so
                switching report resets the measure the reader had chosen on the previous one —
                `net` in REP-02 and `net` in REP-05 are different questions. */}
            <ReportChart
              key={data.key}
              table={data}
              heading={(key) => reportHeading(t, data.key ?? '', key)}
            />

            <div style={{ marginBlockStart: 'var(--space-5)' }}>
              <ReportTable table={data} heading={(key) => reportHeading(t, data.key ?? '', key)} />
            </div>
          </section>
        )}
      </AsyncSection>
    </>
  );
}

/** Hands the bytes to the browser and releases the object URL once it has them. */
function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

/** The month just gone — what somebody opening this screen almost always wants (Journey 7). */
function defaultMonth(): { from: string; to: string } {
  const now = new Date();
  const first = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const last = new Date(now.getFullYear(), now.getMonth(), 0);
  return { from: iso(first), to: iso(last) };
}

function iso(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}
